import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, money, computeTotals } from "@/lib/apiClient";
import { StatusBadge } from "@/components/Common";
import { ComplianceNote } from "@/components/AppShell";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Save, GripVertical, BookOpen } from "lucide-react";

const emptyLine = () => ({
  id: crypto.randomUUID(), description: "", unit: "each", qty: 1, unit_price: 0, taxable: true,
});

const blankQuote = (profile) => ({
  doc_type: "quote",
  client_id: "",
  title: "",
  job_address: "",
  valid_until: "",
  line_items: [emptyLine()],
  custom_fields: [],
  discount_type: "none",
  discount_value: 0,
  prices_include_gst: profile?.prices_include_gst ?? true,
  gst_rate: profile?.gst_rate ?? 10,
  cover_note: profile?.default_cover_note || "",
  terms: profile?.default_terms || "",
  status: "draft",
});

const cell = "w-full border-none px-2 py-2 text-sm bg-transparent focus:outline-none focus:bg-blue-50/60 transition-colors";

export default function QuoteBuilder() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [clients, setClients] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [q, setQ] = useState(null);
  const [meta, setMeta] = useState({ number: "New", status: "draft" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const [p, c, cat] = await Promise.all([api.get("/profile"), api.get("/clients"), api.get("/catalog")]);
      setProfile(p.data);
      setClients(c.data);
      setCatalog(cat.data);
      if (isNew) {
        setQ(blankQuote(p.data));
      } else {
        const { data } = await api.get(`/quotes/${id}`);
        setQ(data);
        setMeta({ number: data.number, status: data.status });
      }
    })().catch(() => toast.error("Could not load document"));
  }, [id, isNew]);

  const totals = useMemo(() => (q ? computeTotals(q) : null), [q]);
  if (!q) return <div className="text-sm text-slate-500">Loading…</div>;

  const set = (k, v) => setQ((s) => ({ ...s, [k]: v }));
  const setLine = (lid, k, v) =>
    setQ((s) => ({ ...s, line_items: s.line_items.map((l) => (l.id === lid ? { ...l, [k]: v } : l)) }));
  const addLine = () => setQ((s) => ({ ...s, line_items: [...s.line_items, emptyLine()] }));
  const removeLine = (lid) =>
    setQ((s) => ({ ...s, line_items: s.line_items.filter((l) => l.id !== lid) }));
  const addFromCatalog = (itemId) => {
    const it = catalog.find((c) => c.id === itemId);
    if (!it) return;
    setQ((s) => ({
      ...s,
      line_items: [...s.line_items, {
        id: crypto.randomUUID(), description: it.name + (it.description ? ` — ${it.description}` : ""),
        unit: it.unit, qty: 1, unit_price: it.unit_price, taxable: it.taxable,
      }],
    }));
  };
  const addCustomField = () =>
    setQ((s) => ({ ...s, custom_fields: [...s.custom_fields, { id: crypto.randomUUID(), label: "", value: "" }] }));
  const setCustomField = (fid, k, v) =>
    setQ((s) => ({ ...s, custom_fields: s.custom_fields.map((f) => (f.id === fid ? { ...f, [k]: v } : f)) }));
  const removeCustomField = (fid) =>
    setQ((s) => ({ ...s, custom_fields: s.custom_fields.filter((f) => f.id !== fid) }));

  const payload = () => ({
    doc_type: q.doc_type,
    client_id: q.client_id || null,
    title: q.title,
    job_address: q.job_address,
    valid_until: q.valid_until || "",
    line_items: q.line_items.map((l) => ({
      id: l.id, description: l.description, unit: l.unit,
      qty: Number(l.qty) || 0, unit_price: Number(l.unit_price) || 0, taxable: l.taxable !== false,
    })),
    custom_fields: q.custom_fields,
    discount_type: q.discount_type,
    discount_value: Number(q.discount_value) || 0,
    prices_include_gst: !!q.prices_include_gst,
    gst_rate: Number(q.gst_rate) || 0,
    cover_note: q.cover_note,
    terms: q.terms,
    status: q.status,
  });

  const save = async () => {
    setSaving(true);
    try {
      if (isNew) {
        const { data } = await api.post("/quotes", payload());
        toast.success(`${data.number} created`);
        navigate(`/quotes/${data.id}`, { replace: true });
      } else {
        const { data } = await api.put(`/quotes/${id}`, payload());
        setMeta({ number: data.number, status: data.status });
        toast.success("Saved");
      }
    } catch {
      toast.error("Could not save document");
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (status) => {
    if (isNew) { set("status", status); return; }
    const { data } = await api.patch(`/quotes/${id}/status`, { status });
    setQ((s) => ({ ...s, status: data.status }));
    setMeta((m) => ({ ...m, status: data.status }));
    toast.success(`Marked ${status}`);
  };

  const currency = profile?.currency || "AUD";

  return (
    <div data-testid="quote-builder-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/quotes")} aria-label="Back"
            className="p-2 rounded-md text-slate-500 hover:bg-slate-100 transition-colors">
            <ArrowLeft size={18} />
          </button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
              {isNew ? "New document" : meta.number}
            </h1>
            <div className="flex items-center gap-2 mt-1.5">
              <StatusBadge status={q.status} />
              <span className="text-xs text-slate-400 uppercase tracking-wide">{q.doc_type}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select data-testid="status-select" value={q.status} onChange={(e) => changeStatus(e.target.value)}
            className="border border-slate-200 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
            {["draft", "sent", "viewed", "accepted", "rejected"].map((s) => (
              <option key={s} value={s} className="capitalize">{s}</option>
            ))}
          </select>
          <button data-testid="save-quote-btn" onClick={save} disabled={saving}
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white rounded-md px-4 py-2 text-sm font-medium transition-colors active:scale-95">
            <Save size={16} /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      {/* Quote vs Estimate toggle */}
      <div className="bg-white border border-slate-200 shadow-sm rounded-md p-5 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center gap-5">
          <div className="inline-flex rounded-md border border-slate-200 p-1 bg-slate-50" data-testid="doc-type-toggle">
            {["quote", "estimate"].map((t) => (
              <button key={t} data-testid={`doc-type-${t}`} onClick={() => set("doc_type", t)}
                className={`px-4 py-1.5 rounded text-sm font-semibold capitalize transition-colors ${
                  q.doc_type === t ? "bg-blue-600 text-white" : "text-slate-600 hover:text-slate-900"
                }`}>
                {t}
              </button>
            ))}
          </div>
          <p className="text-xs text-slate-500 leading-relaxed max-w-2xl" data-testid="doc-type-explainer">
            {q.doc_type === "quote"
              ? "Quote — a fixed price. Once the client accepts, it is binding on you under Australian Consumer Law."
              : "Estimate — an approximation only, not binding. The final price may differ; tell the client how it may change."}
          </p>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        <div className="flex-1 space-y-6 min-w-0">
          {/* Job details */}
          <div className="bg-white border border-slate-200 shadow-sm rounded-md p-5">
            <h2 className="font-semibold text-slate-900 mb-4">Job details</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="block">
                <span className="text-xs font-medium text-slate-600">Client</span>
                <select data-testid="client-select" value={q.client_id || ""}
                  onChange={(e) => {
                    const cid = e.target.value;
                    const c = clients.find((x) => x.id === cid);
                    setQ((s) => ({ ...s, client_id: cid, job_address: s.job_address || c?.job_address || "" }));
                  }}
                  className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">Select a client…</option>
                  {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-xs font-medium text-slate-600">Job title</span>
                <input data-testid="quote-title-input" value={q.title} onChange={(e) => set("title", e.target.value)}
                  placeholder="Bathroom re-pipe — Stage 1"
                  className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </label>
              <label className="block">
                <span className="text-xs font-medium text-slate-600">Job address</span>
                <input data-testid="quote-address-input" value={q.job_address} onChange={(e) => set("job_address", e.target.value)}
                  className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </label>
              <label className="block">
                <span className="text-xs font-medium text-slate-600">Valid until</span>
                <input data-testid="quote-valid-until-input" type="date" value={q.valid_until || ""}
                  onChange={(e) => set("valid_until", e.target.value)}
                  className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </label>
            </div>

            <div className="mt-5 pt-5 border-t border-slate-100">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Custom fields</span>
                <button data-testid="add-custom-field-btn" onClick={addCustomField}
                  className="text-xs font-medium text-blue-600 hover:text-blue-700 inline-flex items-center gap-1">
                  <Plus size={13} /> Add field
                </button>
              </div>
              {q.custom_fields.length === 0 ? (
                <p className="text-xs text-slate-400">No custom fields — add things like Permit No. or Site contact.</p>
              ) : (
                <div className="space-y-2">
                  {q.custom_fields.map((f) => (
                    <div key={f.id} className="flex items-center gap-2" data-testid={`custom-field-${f.id}`}>
                      <input placeholder="Label" value={f.label} onChange={(e) => setCustomField(f.id, "label", e.target.value)}
                        className="w-1/3 border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      <input placeholder="Value" value={f.value} onChange={(e) => setCustomField(f.id, "value", e.target.value)}
                        className="flex-1 border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      <button onClick={() => removeCustomField(f.id)} aria-label="Remove field"
                        className="p-2 rounded-md text-slate-400 hover:bg-red-50 hover:text-red-600 transition-colors">
                        <Trash2 size={15} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Line items spreadsheet */}
          <div className="bg-white border border-slate-200 shadow-sm rounded-md overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-slate-200">
              <h2 className="font-semibold text-slate-900">Line items</h2>
              <div className="flex items-center gap-2">
                <select data-testid="catalog-picker" value="" onChange={(e) => addFromCatalog(e.target.value)}
                  className="border border-slate-200 rounded-md px-3 py-1.5 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">＋ From catalog…</option>
                  {catalog.map((c) => <option key={c.id} value={c.id}>{c.name} — {money(c.unit_price, currency)}</option>)}
                </select>
                <button data-testid="add-line-item-btn" onClick={addLine}
                  className="inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white rounded-md px-3 py-1.5 text-xs font-semibold transition-colors">
                  <Plus size={14} /> Add row
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] text-sm border-collapse" data-testid="line-items-table">
                <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="w-8" />
                    <th className="text-left font-semibold px-2 py-2.5">Description</th>
                    <th className="text-left font-semibold px-2 py-2.5 w-24">Unit</th>
                    <th className="text-right font-semibold px-2 py-2.5 w-20">Qty</th>
                    <th className="text-right font-semibold px-2 py-2.5 w-28">Rate</th>
                    <th className="text-center font-semibold px-2 py-2.5 w-16">GST</th>
                    <th className="text-right font-semibold px-3 py-2.5 w-28">Amount</th>
                    <th className="w-10" />
                  </tr>
                </thead>
                <tbody>
                  {q.line_items.map((l, idx) => (
                    <tr key={l.id} data-testid={`line-item-row-${idx}`} className="border-b border-slate-100 last:border-0">
                      <td className="text-center text-slate-300"><GripVertical size={14} className="mx-auto" /></td>
                      <td className="border-l border-slate-100">
                        <input data-testid={`line-description-${idx}`} className={cell} value={l.description}
                          placeholder="What are you doing?" onChange={(e) => setLine(l.id, "description", e.target.value)} />
                      </td>
                      <td className="border-l border-slate-100">
                        <input data-testid={`line-unit-${idx}`} className={cell} value={l.unit}
                          onChange={(e) => setLine(l.id, "unit", e.target.value)} />
                      </td>
                      <td className="border-l border-slate-100">
                        <input data-testid={`line-qty-${idx}`} type="number" step="0.01" className={`${cell} text-right`}
                          value={l.qty} onChange={(e) => setLine(l.id, "qty", e.target.value)} />
                      </td>
                      <td className="border-l border-slate-100">
                        <input data-testid={`line-price-${idx}`} type="number" step="0.01" className={`${cell} text-right`}
                          value={l.unit_price} onChange={(e) => setLine(l.id, "unit_price", e.target.value)} />
                      </td>
                      <td className="border-l border-slate-100 text-center">
                        <input data-testid={`line-taxable-${idx}`} type="checkbox" checked={l.taxable !== false}
                          onChange={(e) => setLine(l.id, "taxable", e.target.checked)} className="h-4 w-4 accent-blue-600" />
                      </td>
                      <td className="border-l border-slate-100 px-3 text-right font-semibold text-slate-900"
                        data-testid={`line-amount-${idx}`}>
                        {money((Number(l.qty) || 0) * (Number(l.unit_price) || 0), currency)}
                      </td>
                      <td className="text-center">
                        <button data-testid={`remove-line-${idx}`} onClick={() => removeLine(l.id)} aria-label="Remove line"
                          className="p-1.5 rounded text-slate-300 hover:text-red-600 transition-colors">
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {catalog.length === 0 && (
              <div className="flex items-center gap-2 px-5 py-3 border-t border-slate-100 text-xs text-slate-500">
                <BookOpen size={14} /> Tip: save common items in the Price Catalog to fill rows instantly.
              </div>
            )}
          </div>

          {/* Notes & terms */}
          <div className="bg-white border border-slate-200 shadow-sm rounded-md p-5 grid grid-cols-1 lg:grid-cols-2 gap-5">
            <label className="block">
              <span className="text-xs font-medium text-slate-600">Cover note</span>
              <textarea data-testid="cover-note-input" rows={5} value={q.cover_note} onChange={(e) => set("cover_note", e.target.value)}
                placeholder="Thanks for the opportunity to quote…"
                className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-slate-600">Terms &amp; conditions</span>
              <textarea data-testid="terms-input" rows={5} value={q.terms} onChange={(e) => set("terms", e.target.value)}
                placeholder="Payment terms, deposit, exclusions…"
                className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </label>
          </div>
        </div>

        {/* Summary panel */}
        <aside className="w-full lg:w-80 shrink-0 space-y-4 lg:sticky lg:top-6 h-fit">
          <div className="bg-slate-50 border border-slate-200 rounded-md p-6" data-testid="totals-panel">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-4">Totals</p>
            <div className="space-y-2.5 text-sm">
              <div className="flex justify-between text-slate-600">
                <span>Subtotal (ex GST)</span>
                <span data-testid="subtotal-value" className="font-medium text-slate-900">{money(totals.subtotal_ex_gst, currency)}</span>
              </div>
              {totals.discount_amount > 0 && (
                <div className="flex justify-between text-slate-600">
                  <span>Discount</span>
                  <span data-testid="discount-value" className="font-medium text-red-600">−{money(totals.discount_amount, currency)}</span>
                </div>
              )}
              <div className="flex justify-between text-slate-600">
                <span>GST ({q.gst_rate}%)</span>
                <span data-testid="gst-value" className="font-medium text-slate-900">{money(totals.gst_amount, currency)}</span>
              </div>
              <div className="pt-3 mt-3 border-t border-slate-200">
                <p className="text-xs uppercase tracking-wide text-slate-500">Total inc. GST</p>
                <p data-testid="total-value" className="text-3xl font-black tracking-tighter text-slate-900 mt-1"
                  style={{ fontFamily: "Manrope, sans-serif" }}>
                  {money(totals.total_inc_gst, currency)}
                </p>
              </div>
            </div>

            <div className="mt-6 pt-5 border-t border-slate-200 space-y-4">
              <label className="flex items-start gap-2.5">
                <input data-testid="prices-include-gst-toggle" type="checkbox" checked={!!q.prices_include_gst}
                  onChange={(e) => set("prices_include_gst", e.target.checked)} className="h-4 w-4 mt-0.5 accent-blue-600" />
                <span className="text-xs text-slate-700 leading-relaxed">
                  Entered rates <strong>include GST</strong> (recommended for consumers)
                </span>
              </label>
              <label className="block">
                <span className="text-xs font-medium text-slate-600">GST rate %</span>
                <input data-testid="gst-rate-input" type="number" step="0.1" value={q.gst_rate}
                  onChange={(e) => set("gst_rate", e.target.value)}
                  className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </label>
              <div className="grid grid-cols-2 gap-2">
                <label className="block">
                  <span className="text-xs font-medium text-slate-600">Discount</span>
                  <select data-testid="discount-type-select" value={q.discount_type} onChange={(e) => set("discount_type", e.target.value)}
                    className="mt-1 w-full border border-slate-200 rounded-md px-2 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="none">None</option>
                    <option value="percent">%</option>
                    <option value="fixed">Fixed</option>
                  </select>
                </label>
                <label className="block">
                  <span className="text-xs font-medium text-slate-600">Value</span>
                  <input data-testid="discount-value-input" type="number" step="0.01" value={q.discount_value}
                    disabled={q.discount_type === "none"}
                    onChange={(e) => set("discount_value", e.target.value)}
                    className="mt-1 w-full border border-slate-200 rounded-md px-2 py-2 text-sm bg-white disabled:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </label>
              </div>
            </div>

            <p className="mt-5 text-[11px] text-slate-500 leading-relaxed" data-testid="gst-statement">
              {q.prices_include_gst
                ? "Displayed prices include GST. Total price payable is shown inc. GST."
                : "Prices exclude GST. GST will be added to the total shown above."}
            </p>
          </div>

          {profile && !(profile.abn || "").trim() && (
            <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-xs text-red-700" data-testid="abn-missing-note">
              No ABN on your business profile. Add it to issue compliant tax invoices.
            </div>
          )}
          <ComplianceNote />
        </aside>
      </div>
    </div>
  );
}
